import { useRef, useState } from "react";
import { MousePointer2, ArrowUpRight, X as XIcon, Circle, Highlighter, Trash2, Check } from "lucide-react";

const RED = "#A83232";
const GREEN = "#7E856C";
const TOOLS = [
  { key: "select", label: "Vælg", icon: MousePointer2 },
  { key: "arrow", label: "Pil", icon: ArrowUpRight },
  { key: "cross", label: "Kryds", icon: XIcon },
  { key: "check", label: "Flueben", icon: Check },
  { key: "circle", label: "Cirkel", icon: Circle },
  { key: "highlight", label: "Marker", icon: Highlighter },
];

export function AnnotationLayer({ annotations = [] }) {
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
      <defs>
        <marker id="qr-arrow-head" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L6,3 L0,6 Z" fill={RED} />
        </marker>
      </defs>
      {annotations.map((a) => <Shape key={a.id} a={a} />)}
    </svg>
  );
}

function Shape({ a, selected, onPointerDown }) {
  const common = { vectorEffect: "non-scaling-stroke", onPointerDown, style: onPointerDown ? { cursor: "move" } : undefined };
  const sw = selected ? 3 : 2;
  if (a.kind === "arrow")
    return <line x1={a.x} y1={a.y} x2={a.x2} y2={a.y2} stroke={RED} strokeWidth={sw} markerEnd="url(#qr-arrow-head)" {...common} />;
  if (a.kind === "cross") {
    const s = 3.5;
    return <g {...common}>
      <line x1={a.x - s} y1={a.y - s} x2={a.x + s} y2={a.y + s} stroke={RED} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
      <line x1={a.x - s} y1={a.y + s} x2={a.x + s} y2={a.y - s} stroke={RED} strokeWidth={sw} vectorEffect="non-scaling-stroke" />
    </g>;
  }
  if (a.kind === "check") {
    const s = 3.5;
    return <polyline points={`${a.x - s},${a.y} ${a.x - s / 3},${a.y + s} ${a.x + s},${a.y - s}`}
      fill="none" stroke={GREEN} strokeWidth={sw + 0.5} strokeLinecap="round" strokeLinejoin="round" {...common} />;
  }
  if (a.kind === "circle") {
    const cx = (a.x + a.x2) / 2, cy = (a.y + a.y2) / 2;
    return <ellipse cx={cx} cy={cy} rx={Math.abs(a.x2 - a.x) / 2} ry={Math.abs(a.y2 - a.y) / 2} fill="none" stroke={RED} strokeWidth={sw} {...common} />;
  }
  if (a.kind === "highlight") {
    return <rect x={Math.min(a.x, a.x2)} y={Math.min(a.y, a.y2)} width={Math.abs(a.x2 - a.x)} height={Math.abs(a.y2 - a.y)}
      fill={RED} fillOpacity={0.28} stroke={selected ? RED : "none"} strokeWidth={selected ? 2 : 0} {...common} />;
  }
  return null;
}

export default function ImageAnnotator({ src, value = [], onChange }) {
  const [tool, setTool] = useState("select");
  const [selectedId, setSelectedId] = useState(null);
  const draft = useRef(null);
  const drag = useRef(null);
  const svgRef = useRef(null);
  const [, force] = useState(0);

  const pt = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100)),
    };
  };

  const startDraw = (e) => {
    if (tool === "select") { setSelectedId(null); return; }
    e.preventDefault();
    const p = pt(e);
    if (tool === "cross" || tool === "check") {
      onChange([...value, { id: Date.now().toString(36), kind: tool, x: p.x, y: p.y }]);
      return;
    }
    draft.current = { id: Date.now().toString(36), kind: tool, x: p.x, y: p.y, x2: p.x, y2: p.y };
    svgRef.current.setPointerCapture(e.pointerId);
  };

  const startDrag = (e, a) => {
    if (tool !== "select") return;
    e.stopPropagation();
    setSelectedId(a.id);
    drag.current = { id: a.id, start: pt(e), orig: { ...a } };
    svgRef.current.setPointerCapture(e.pointerId);
  };

  const move = (e) => {
    if (draft.current) {
      const p = pt(e);
      draft.current = { ...draft.current, x2: p.x, y2: p.y };
      force((n) => n + 1);
    } else if (drag.current) {
      const p = pt(e);
      const dx = p.x - drag.current.start.x, dy = p.y - drag.current.start.y;
      const o = drag.current.orig;
      const patch = { x: o.x + dx, y: o.y + dy };
      if (o.x2 !== undefined) { patch.x2 = o.x2 + dx; patch.y2 = o.y2 + dy; }
      onChange(value.map((a) => a.id === drag.current.id ? { ...a, ...patch } : a));
    }
  };

  const end = () => {
    if (draft.current) {
      const d = draft.current;
      const big = Math.abs(d.x2 - d.x) + Math.abs(d.y2 - d.y) > 3;
      if (big) onChange([...value, d]);
      draft.current = null;
      force((n) => n + 1);
    }
    drag.current = null;
  };

  const del = () => { if (selectedId) { onChange(value.filter((a) => a.id !== selectedId)); setSelectedId(null); } };

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {TOOLS.map((t) => (
          <button key={t.key} type="button" data-testid={`annot-tool-${t.key}`} onClick={() => { setTool(t.key); setSelectedId(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              tool === t.key ? "bg-[#F1DEDA] border-[#E9C4C4] text-[#A83232]" : "bg-white border-[#EBE8E3] text-stone-600 hover:bg-[#F3F1EC]"
            }`}>
            <t.icon className="h-3.5 w-3.5" strokeWidth={2} /> {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <button type="button" data-testid="annot-delete" onClick={del} disabled={!selectedId}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[#EBE8E3] text-[#A83232] disabled:opacity-40 hover:bg-[#F1DEDA]">
          <Trash2 className="h-3.5 w-3.5" /> Slet valgt
        </button>
      </div>
      <div className="relative select-none rounded-xl overflow-hidden border border-[#EBE8E3] bg-[#F3F1EC]" style={{ touchAction: "none" }}>
        <img src={src} alt="annotering" className="block w-full h-auto pointer-events-none" draggable={false} />
        <svg ref={svgRef} viewBox="0 0 100 100" preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full" style={{ cursor: tool === "select" ? "default" : "crosshair" }}
          onPointerDown={startDraw} onPointerMove={move} onPointerUp={end} onPointerLeave={end} data-testid="annot-canvas">
          <defs>
            <marker id="qr-arrow-head" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L6,3 L0,6 Z" fill={RED} />
            </marker>
          </defs>
          {value.map((a) => <Shape key={a.id} a={a} selected={selectedId === a.id} onPointerDown={(e) => startDrag(e, a)} />)}
          {draft.current && <Shape a={draft.current} />}
        </svg>
      </div>
      <p className="text-xs text-stone-400 mt-2">Vælg et værktøj og træk hen over billedet. Med "Vælg" kan du flytte og slette markeringer.</p>
    </div>
  );
}
